import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, NgZone, OnDestroy, OnInit, Output } from '@angular/core';

import { Observable, Subject, Subscription, debounceTime, distinctUntilChanged, forkJoin, map, tap } from 'rxjs';
import { Accordion } from 'primeng/accordion';

import { Tag } from '@commons/models/domain/Tag';
import { AreaListFilterRequest } from '@commons/models/remote.request/AreaListFilterRequest';
import { GetTagsListByFilterResponse, IUntaggedAreas } from '@commons/models/remote.response/GetTagsListByFilterResponse';
import { WebAccessService } from '@commons/services/web.access/web-access.service';
import { Area } from '@commons/models/domain/Area';
import { AreaDrawerService } from '@commons/services/map/drawer/area/area-drawer.service';
import { AreaService } from '@commons/services/area/area.service';

interface IGroup {
  tag: Tag;
  isChecked: boolean;
  isLoadingAreas: boolean;
  areas?: IAreaInGroupList[];
}

export interface IAreaInGroupList extends Area {
  isChecked?: boolean;
}

interface IShowArea {
  area: Area;
  groupId?: number;
  isUntaggedArea?: boolean;
  withOutRemove?: boolean;
  withOutCenter?: boolean;
  editable?: boolean;
}

interface IGroupAreaActiveMap {
  areaId: number;
  layerMapRef: any;
}

@Component({
  selector: 'app-areas-list',
  templateUrl: './areas-list.component.html',
  styleUrls: ['./areas-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AreasListComponent implements OnInit, OnDestroy {
  @Output() public addArea$: EventEmitter<Area | void> = new EventEmitter();
  @Output() public close$: EventEmitter<void> = new EventEmitter();

  public isLoadingGroups: boolean = true;
  public groups: IGroup[] = [];
  public activeGroups: number[] = []; // otwarte accordiony
  public untaggedAreas: IAreaInGroupList[] = [];

  // obszary zaznaczone na mapie
  public areasInMapActiveIds: {
    [key: number]: IGroupAreaActiveMap[]; //groups
    untaggedAreas?: IGroupAreaActiveMap[]; //untagged areas
  } = {};
  public isErrorMax500AreasVisible: boolean;
  private numberOfMarkedAreasOnTheMap: number = 0;

  public searchValue: string;
  public onChangeSearchValue$: Subject<string> = new Subject();

  private readonly ComponentSubsc: Subscription = new Subscription();
  private readonly MAX_SELECTED_AREAS_IN_MAP = 500;

  constructor(
    private webAccess: WebAccessService,
    private changeDetRef: ChangeDetectorRef,
    private areaDrawerService: AreaDrawerService,
    private ngZone: NgZone,
    private areaService: AreaService,
  ) { }

  ngOnInit(): void {
    this.getListData();
    this.onChangeSearchValue();
    this.onClickEditOnTooltip();
    this.onClickDeleteOnTooltip();
  }

  ngOnDestroy(): void {
    this.ComponentSubsc.unsubscribe();
  }

  public onClickEditOnTooltip(): void {
    const sub = this.areaDrawerService.onClickEditTooltipArea$.pipe(debounceTime(0)).subscribe(
      (area) => {
        this.onEditArea(area);
      }
    );
    this.ComponentSubsc.add(sub);
  }

  public onClickDeleteOnTooltip(): void {
    const sub = this.areaDrawerService.onClickDeleteTooltipArea$.pipe(debounceTime(0)).subscribe(
      (area) => {
        const hasAreaGroup = area.Tg.length;
        // delete area with groups
        if (hasAreaGroup) {
          const groupsIds = [...area.Tg].map(item => item.L);

          for (const groupId of groupsIds) {
            const groupData = this.groups.find(el => el.tag.L === groupId);

            if (groupData?.areas?.length) {
              const indexArea = groupData.areas.findIndex(areaData => areaData.Id === area.Id);

              if (indexArea !== -1) {
                this.deleteAreaInGroup(area, groupData.tag.Id, indexArea);
                this.changeDetRef.markForCheck();
              }
            }
          }
          return;
        }
        // delete untaggedArea
        this.deleteUntaggedArea(area);
        this.changeDetRef.markForCheck();
      }
    );
    this.ComponentSubsc.add(sub);
  }

  public onChangeSearchValue(): void {
    const sub = this.onChangeSearchValue$
    .pipe(
      tap(() => this.isLoadingGroups = true),
      distinctUntilChanged(),
      debounceTime(300),
    )
    .subscribe(
      () => {
        this.getListData();
      }
    );
    this.ComponentSubsc.add(sub);
  }

  public onEditArea(area: Area): void {
    this.addArea$.emit(area);
  }

  public deleteUntaggedArea(area: Area): void {
    this.removeUntaggedAreaFromMap(area);
    this.deleteUntaggedAreaFromList(area);
    this.deleteArea(area.Id);
  }

  public deleteAreaInGroup(area: Area, groupId: number, areaIndex: number): void {
    this.removeAreaFromMap(area, groupId);
    this.deleteAreaFromGroupList(groupId, areaIndex);
    this.deleteArea(area.Id);
  }

  private deleteArea(areaId: number): void {
    const sub = this.areaService.deleteAreaDetails$(areaId).subscribe(() => {});
    this.ComponentSubsc.add(sub);
  }

  private deleteAreaFromGroupList(groupId: number, areaIndexInGroup: number): void {
    let item = this.groups.find(item => item.tag.Id === groupId);
    if (item) {
      item = {
        ...item,
        areas: item.areas?.splice(areaIndexInGroup, 1),
      };
    }
  }

  private deleteUntaggedAreaFromList(area: Area): void {
    const index = this.untaggedAreas.findIndex(item => item.Id === area.Id);

    if (index !== -1) {
      this.untaggedAreas.splice(index, 1);
    }
  }

  public onClickClose(): void {
    this.clearAllAreasFromMap();
    this.close$.emit();
  }

  private addOneNumberMarkedAreas(): void {
    this.numberOfMarkedAreasOnTheMap = this.numberOfMarkedAreasOnTheMap + 1;
  }

  private removeOneNumberMarkedAreas(): void {
    this.numberOfMarkedAreasOnTheMap = this.numberOfMarkedAreasOnTheMap - 1;
    
    if (this.numberOfMarkedAreasOnTheMap < this.MAX_SELECTED_AREAS_IN_MAP) {
      this.isErrorMax500AreasVisible = false;
    }
  }

  public showArea(data: IShowArea): void {
    if (!data.withOutRemove) {
      this.clearAllAreasFromMap();
    }
    this.areaDrawerService.setArea(data.area, data.editable);
    const areaMapLayerRef = this.areaDrawerService.draw();
    !data.withOutCenter && this.areaDrawerService.fitBoundsToVisibleArea(areaMapLayerRef.getBounds());

    data.isUntaggedArea
      ? this.addUntaggedNewAreaRefToList(data.area, areaMapLayerRef)
      : (data.groupId && this.addNewAreaRefToList(data.area.Id, areaMapLayerRef, data.groupId));

    this.addOneNumberMarkedAreas();
  }

  private addUntaggedNewAreaRefToList(area: Area, layerMapRef: any): void {
    const areaData = {
      areaId: area.Id,
      layerMapRef,
    };
    this.areasInMapActiveIds = {
      ...this.areasInMapActiveIds,
      untaggedAreas: [...(this.areasInMapActiveIds.untaggedAreas || []), areaData],
    }
  }

  public clearAllAreasFromMap(): void {
    this.areaDrawerService.setArea(undefined);
    this.areaDrawerService.remove();
    this.removeAllAreaRefs();
  }

  private removeAllAreaRefs(): void {
    this.areasInMapActiveIds = {};
    this.numberOfMarkedAreasOnTheMap = 0;
  }

  private removeAllAreasSelectedGroup(groupData: IGroup): void {
    const groupAreasRef = this.areasInMapActiveIds[groupData.tag.Id];

    if (groupAreasRef) {
      groupAreasRef.forEach(
        (area) => {
          area.layerMapRef.remove();
          this.removeOneNumberMarkedAreas();
        }
      );
  
      this.areasInMapActiveIds = {
        ...this.areasInMapActiveIds,
        [groupData.tag.Id]: [],
      };
    }
  }

  private addNewAreaRefToList(areaId: number, layerMapRef: any, groupId: number): void {
    const areaData = {
      areaId,
      layerMapRef,
    };

    if (this.areasInMapActiveIds[groupId]) {
      let areasGroup = [...this.areasInMapActiveIds[groupId]]; // []
      areasGroup = [
        ...areasGroup,
        { ...areaData },
      ];
      this.areasInMapActiveIds[groupId] = [...areasGroup];
      return;
    }

    this.areasInMapActiveIds = {
      ...this.areasInMapActiveIds,
      [groupId]: [areaData], 
    };
  }

  public onChangeCheckedStateArea(isChecked: boolean, area: Area, groupId: number): void {
    if (isChecked) {
      if (this.numberOfMarkedAreasOnTheMap >= this.MAX_SELECTED_AREAS_IN_MAP) {
        this.isErrorMax500AreasVisible = true;
        return;
      }
      this.removeUntaggedAreaFromMap(area);
      this.showArea({ area, groupId, withOutRemove: true });
      return;
    }
    this.removeAreaFromMap(area, groupId);
  }

  public onChangeCheckedStateUntaggedArea(isChecked: boolean, area: Area): void {
    // sprawdzic czy jest juz zaznaczony
    if (isChecked) {
      if (this.numberOfMarkedAreasOnTheMap >= this.MAX_SELECTED_AREAS_IN_MAP) {
        this.isErrorMax500AreasVisible = true;
        return;
      }
      this.removeUntaggedAreaFromMap(area);
      this.showArea({ area, withOutRemove: true, isUntaggedArea: true, groupId: null as any });
      return;
    }
    this.removeUntaggedAreaFromMap(area);
  }

  private removeAreaFromMap(area: Area, groupId: number): void {
    // nie z referencji
    if (this.areasInMapActiveIds[groupId]) {
      const areaRefIndex = this.areasInMapActiveIds[groupId].findIndex(item => item.areaId === area.Id);

      if (areaRefIndex !== -1) {
        this.areasInMapActiveIds[groupId][areaRefIndex].layerMapRef.remove();
        this.areasInMapActiveIds[groupId].splice(areaRefIndex, 1);
        this.removeOneNumberMarkedAreas();
      }
    }
  }

  private removeUntaggedAreaFromMap(area: Area): void {
    if (this.areasInMapActiveIds.untaggedAreas) {
      const areaRefIndex = this.areasInMapActiveIds.untaggedAreas.findIndex(item => item.areaId === area.Id);

      if (areaRefIndex !== -1) {
        this.areasInMapActiveIds.untaggedAreas[areaRefIndex].layerMapRef.remove();
        this.areasInMapActiveIds.untaggedAreas.splice(areaRefIndex, 1);
        this.removeOneNumberMarkedAreas();
      }
    }
  }

  public onToggleAllGroupAreasInMap(groupData: IGroup, groupIndex: number): void {
    console.log('onToggleAllGroupAreasInMap', groupData, groupIndex);

    this.ngZone.runOutsideAngular(() => {
      const { isChecked } = groupData;

      const showAreas = (groups = this.getGroupDataByIndex(groupIndex)): void => {
        groups.areas?.forEach(item => item.isChecked = true);
        this.showAllAreasGroupInMap(groups);
        this.ngZone.run(() => {});
      };

      // metoda sluzaca usunieciu obszaru, gdy po edycji obszar zostanie zapisany
      const removeAreaFromUntagged = (groups = this.getGroupDataByIndex(groupIndex)): void => {
        if (this.areasInMapActiveIds.untaggedAreas?.length) {
          groups.areas?.forEach(
            (area) => {
              this.removeUntaggedAreaFromMap(area);
            }
          );
        }
      };

      if (isChecked) {
        const ifGroupHasMoreThen500Areas: boolean = groupData.tag.C > this.MAX_SELECTED_AREAS_IN_MAP;
        if (ifGroupHasMoreThen500Areas) {
          this.isErrorMax500AreasVisible = true;
          return;
        }

        if (!groupData.areas?.length) {
          this.setLoadingAreasStateForGroup(true, groupIndex);
          this.getAreasGroup(groupData, groupIndex).then(
            () => {
              removeAreaFromUntagged();
              showAreas();
            }
          );
          return;
        }
        showAreas(groupData);
        return;
      }

      groupData.areas?.forEach(item => item.isChecked = false);
      this.removeAllAreasSelectedGroup(groupData);
      this.ngZone.run(() => {});
    });
  }

  private showAllAreasGroupInMap(groupData: IGroup): void {
    groupData.areas?.forEach(
      (area) => {
        this.showArea({ area, groupId: groupData.tag.Id, withOutRemove: true, withOutCenter: true });
      }
    );
  }

  private getGroupDataByIndex(groupIndex: number): IGroup {
    return this.groups[groupIndex];
  }

  public onToggleAccordion(event: any, accordionRef: Accordion, index: number | null = null): void {
    event?.originalEvent?.preventDefault();

    const indexGroup = index !== null ? index : event.index;
    const groupData = { ...this.groups[indexGroup] };

    if (groupData.isLoadingAreas) {
      return;
    }

    if (groupData.areas?.length) {
      this.addActiveGroup(indexGroup);
      return;
    }

    this.setLoadingAreasStateForGroup(true, indexGroup);
    this.getAreasGroup(groupData, indexGroup);
  }

  private getAreasGroup(groupData: IGroup, indexGroup: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const body: AreaListFilterRequest = {
        Phrase: '',
        Tags: [{ ...groupData.tag }],
      };
      const sub = this.webAccess.GetAreaListByFilter(body).pipe(map(res => res.Result)).subscribe(
        (data) => {
          setTimeout(() => {
            this.setAreasInGroup(data, indexGroup);
            this.setLoadingAreasStateForGroup(false, indexGroup);
            return resolve();
          }, 2000);
        }
      );
      this.ComponentSubsc.add(sub);
    });
  }

  private setAreaCoords(area: Area): Area {
    const setAreaTp = (area: Area): number => {
      switch (area.Tp) {
        case 5:
          return area.Tp = 1;
        case 3:
          return area.Tp = 2;
        default:
          return area.Tp = 3;
      }
    };
    return {
      ...area,
      Coords: area.P.map((coord) => ({ 
        Lat: coord.Lat.toString().replace(',', '.'),
        Lon: coord.Lon.toString().replace(',', '.'),
      })),
      Tp: setAreaTp(area),
    };
  }

  private setAreasInGroup(areas: Area[], groupIndex: number): void {
    this.ngZone.runOutsideAngular(() => {
      const groups = [...this.groups];
      let group = { ...groups[groupIndex] };
      group = {
        ...group,
        areas: [...areas].map((area) => this.setAreaCoords(area)),
      };
      groups[groupIndex] = { ...group };
      this.groups = [...groups];
      this.addActiveGroup(groupIndex);
      this.changeDetRef.markForCheck();
      this.ngZone.run(() => {});
    });
  }

  private addActiveGroup(index: number): void {
    this.activeGroups = [...this.activeGroups, index];
  }

  public trackByGroups(index: number, item: { tag: Tag }): number {
    return item.tag.Id;
  }

  public trackByAreas(index: number, area: Area): number {
    return area.Id;
  }

  private setLoadingAreasStateForGroup(isLoading: boolean, index: number): void {
    // nie ladne rozwiazanie po referencji ale poprawimy
    this.groups[index].isLoadingAreas = isLoading;
    this.changeDetRef.markForCheck();
  }

  private getListData(): void {
    this.isLoadingGroups = true;
    const request = [
      this.getTags(),
      this.getUntaggedAreas(),
    ];
    const sub = forkJoin(request).subscribe(
      ([tags, untaggedAreas]: any) => {
        this.setTags(tags);
        this.setUntaggedAreas(untaggedAreas.Result);
        this.isLoadingGroups = false;
        this.changeDetRef.markForCheck();
      }
    );
    this.ComponentSubsc.add(sub);
  }

  private getTags(): Observable<GetTagsListByFilterResponse> {
    const body = {
        Phrase: this.searchValue,
        TagType: 2,
    };
    return this.webAccess.GetTagsListByFilter(body);
  }

  private getUntaggedAreas(): Observable<IUntaggedAreas> {
    const body = {
      Phrase: this.searchValue,
    };
    return this.webAccess.GetUntaggedAreas(body);
  }

  private setTags(data: GetTagsListByFilterResponse): void {
    this.groups = [...data.Result].filter(item => item.C).map(item => ({ tag: item, isChecked: false, isLoadingAreas: false }));
  }

  private setUntaggedAreas(data: Area[]): void {
    this.untaggedAreas = data.map(item => ({ ...this.setAreaCoords(item), isChecked: false }));
  }

}
